import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import blogService from './blogService'

const initialState = {
  blogPosts: [],
  blogPost: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: ''
}

// CREATE
export const createBlogPost = createAsyncThunk(
  'blogPosts/create',
  async (data, thunkAPI) => {
    try {
      return await blogService.createBlogPost(data)
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// READ ALL
export const getBlogPosts = createAsyncThunk(
  'blogPosts/getAll',
  async (_, thunkAPI) => {
    try {
      return await blogService.getBlogPosts()
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// READ ONE
export const getBlogPost = createAsyncThunk(
  'blogPosts/getOne',
  async (id, thunkAPI) => {
    try {
      return await blogService.getBlogPost(id)
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// UPDATE
export const updateBlogPost = createAsyncThunk(
  'blogPosts/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await blogService.updateBlogPost(id, data)
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// DELETE
export const deleteBlogPost = createAsyncThunk(
  'blogPosts/delete',
  async (id, thunkAPI) => {
    try {
      return await blogService.deleteBlogPost(id)
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString()
      return thunkAPI.rejectWithValue(message)
    }
  }
)

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: { reset: () => initialState },
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createBlogPost.pending, (state) => { state.isLoading = true })
      .addCase(createBlogPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.blogPosts.push(action.payload)
      })
      .addCase(createBlogPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      // READ ALL
      .addCase(getBlogPosts.pending, (state) => { state.isLoading = true })
      .addCase(getBlogPosts.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.blogPosts = action.payload
      })
      .addCase(getBlogPosts.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      // READ ONE
      .addCase(getBlogPost.pending, (state) => { state.isLoading = true })
      .addCase(getBlogPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.blogPost = action.payload
      })
      .addCase(getBlogPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      // UPDATE
      .addCase(updateBlogPost.pending, (state) => { state.isLoading = true })
      .addCase(updateBlogPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.blogPosts = state.blogPosts.map((p) =>
          p._id === action.payload._id ? action.payload : p
        )
      })
      .addCase(updateBlogPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })

      // DELETE
      .addCase(deleteBlogPost.pending, (state) => { state.isLoading = true })
      .addCase(deleteBlogPost.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        const id = action.payload._id || action.payload.id
        state.blogPosts = state.blogPosts.filter((p) => p._id !== id)
      })
      .addCase(deleteBlogPost.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })
  }
})

export const { reset } = blogSlice.actions
export default blogSlice.reducer
